import { useMemo } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  FiUsers,
  FiFileText,
  FiAlertTriangle,
  FiMessageSquare,
  FiDollarSign,
  FiCheckCircle,
  FiUserCheck,
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  useAllUsers,
  useAllBounties,
  useAllClaims,
  useAllArbitration,
  useAllSupportTickets,
  useAllPaymentIssues,
  useAllTransactions,
} from '../../hooks/useAdmin';
import { useAllVerificationApplications } from '../../hooks/useVerification';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatRelativeTime } from '../../utils/formatters';

/**
 * Convert a Firestore timestamp (or {seconds} object) to a JS Date.
 */
function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  return null;
}

/**
 * Build a cumulative monthly time-series from an array of items.
 * Each item must have a timestamp field.
 * valueFn(item) returns the value to sum per item (default 1 for counting).
 * Returns [{ month: 'Jan 2025', value: 12 }, ...]
 */
function buildMonthlySeries(items, timestampField, valueFn = () => 1) {
  if (!items.length) return [];

  // Collect all dates
  const dated = items
    .map((item) => ({ date: toDate(item[timestampField]), item }))
    .filter((d) => d.date)
    .sort((a, b) => a.date - b.date);

  if (!dated.length) return [];

  // Group by month
  const monthMap = new Map();
  for (const { date, item } of dated) {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    monthMap.set(key, (monthMap.get(key) || 0) + valueFn(item));
  }

  // Fill in missing months between first and last
  const firstDate = dated[0].date;
  const lastDate = dated[dated.length - 1].date;
  const allMonths = [];
  const cursor = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  const end = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1);

  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    allMonths.push(key);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  // Build cumulative series
  let cumulative = 0;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return allMonths.map((key) => {
    cumulative += monthMap.get(key) || 0;
    const [year, month] = key.split('-');
    return {
      month: `${monthNames[parseInt(month, 10) - 1]} ${year}`,
      value: cumulative,
    };
  });
}

function StatCard({ label, value, helpText, icon, color, to }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const content = (
    <Stat>
      <HStack spacing={3} mb={2}>
        <Box p={2} rounded="md" bg={`${color}.50`}>
          <Icon as={icon} boxSize={5} color={`${color}.500`} />
        </Box>
        <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
      </HStack>
      <StatNumber fontSize="2xl">{value}</StatNumber>
      {helpText && <StatHelpText mb={0}>{helpText}</StatHelpText>}
    </Stat>
  );

  return (
    <Box
      as={to ? Link : 'div'}
      to={to}
      bg={bg}
      p={5}
      rounded="xl"
      borderWidth={1}
      borderColor={borderColor}
      _hover={to ? { borderColor: `${color}.300`, shadow: 'sm' } : undefined}
      transition="all 0.2s"
    >
      {content}
    </Box>
  );
}

function ChartCard({ title, data, color, valueLabel, formatValue }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
      <Text fontWeight="semibold" fontSize="sm" mb={4}>{title}</Text>
      {data.length < 2 ? (
        <Box py={8} textAlign="center">
          <Text fontSize="sm" color="gray.400">Not enough data yet</Text>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#a0aec0' }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#a0aec0' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickFormatter={formatValue}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '13px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              formatter={(v) => [formatValue ? formatValue(v) : v, valueLabel]}
              labelStyle={{ fontWeight: 600, marginBottom: 4 }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${color})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}

function RecentItem({ label, sublabel, time, to }) {
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  return (
    <Box
      as={to ? Link : 'div'}
      to={to}
      display="block"
      py={2}
      px={3}
      borderBottomWidth={1}
      borderColor={borderColor}
      _hover={to ? { bg: hoverBg } : undefined}
      transition="all 0.15s"
    >
      <HStack justify="space-between">
        <Box>
          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{label}</Text>
          {sublabel && <Text fontSize="xs" color="gray.500">{sublabel}</Text>}
        </Box>
        <Text fontSize="xs" color="gray.400" flexShrink={0}>{time}</Text>
      </HStack>
    </Box>
  );
}

export default function AdminDashboard() {
  const { users, loading: usersLoading } = useAllUsers();
  const { bounties, loading: bountiesLoading } = useAllBounties();
  const { claims, loading: claimsLoading } = useAllClaims();
  const { cases, loading: casesLoading } = useAllArbitration();
  const { tickets, loading: ticketsLoading } = useAllSupportTickets();
  const { issues, loading: paymentsLoading } = useAllPaymentIssues();
  const { transactions, loading: transactionsLoading } = useAllTransactions();
  const { applications: verifications, loading: verificationsLoading } = useAllVerificationApplications();

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const loading = usersLoading || bountiesLoading || claimsLoading || casesLoading || ticketsLoading || paymentsLoading || transactionsLoading || verificationsLoading;

  // Build chart data
  const usersOverTime = useMemo(() => buildMonthlySeries(users, 'createdAt'), [users]);
  const bountiesOverTime = useMemo(() => buildMonthlySeries(bounties, 'createdAt'), [bounties]);
  const settledOverTime = useMemo(() => {
    const completed = bounties.filter((b) => b.status === 'completed' && b.completedAt);
    return buildMonthlySeries(completed, 'completedAt', (b) => (b.rewardAmount || 0) / 100);
  }, [bounties]);

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const openBounties = bounties.filter((b) => b.status === 'open').length;
  const completedBounties = bounties.filter((b) => b.status === 'completed').length;
  const openDisputes = cases.filter((c) => c.status === 'open').length;
  const openTickets = tickets.filter((t) => t.status === 'open').length;
  const pendingPayments = issues.filter((i) => i.status === 'open' || i.status === 'pending').length;
  const pendingVerifications = verifications.filter((v) => v.status === 'pending').length;

  // Payment stats
  const totalRevenue = transactions.reduce((sum, t) => sum + (t.totalCharged || t.amount || 0), 0);
  const totalFees = transactions.reduce((sum, t) => sum + (t.feeAmount || 0), 0);

  const formatDollar = (v) => `$${Number(v).toLocaleString()}`;

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Heading size="lg">Admin Dashboard</Heading>
        <Text color="gray.500" mt={1}>Site overview and management</Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        <StatCard
          label="Total Users"
          value={users.length}
          icon={FiUsers}
          color="blue"
          to="/admin/users"
        />
        <StatCard
          label="Total Tasks"
          value={bounties.length}
          helpText={`${openBounties} open, ${completedBounties} completed`}
          icon={FiFileText}
          color="green"
          to="/admin/tasks"
        />
        <StatCard
          label="Total Submissions"
          value={claims.length}
          helpText={`${claims.filter((c) => c.status === 'pending').length} pending`}
          icon={FiCheckCircle}
          color="purple"
        />
        <StatCard
          label="Open Disputes"
          value={openDisputes}
          helpText={`${cases.length} total`}
          icon={FiAlertTriangle}
          color="orange"
          to="/admin/disputes"
        />
        <StatCard
          label="Open Tickets"
          value={openTickets}
          helpText={`${tickets.length} total`}
          icon={FiMessageSquare}
          color="teal"
          to="/admin/support"
        />
        <StatCard
          label="Pending Verifications"
          value={pendingVerifications}
          helpText={`${verifications.length} total applications`}
          icon={FiUserCheck}
          color="cyan"
          to="/admin/verifications"
        />
        <StatCard
          label="Revenue Processed"
          value={`$${(totalRevenue / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          helpText={`${transactions.length} transactions`}
          icon={FiDollarSign}
          color="green"
          to="/admin/payments"
        />
        <StatCard
          label="Fees Collected"
          value={`$${(totalFees / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          helpText={`From ${transactions.filter(t => t.feeAmount > 0).length} payments`}
          icon={FiDollarSign}
          color="yellow"
          to="/admin/payments"
        />
        <StatCard
          label="Failed Payments"
          value={pendingPayments}
          helpText={`${issues.length} total issues`}
          icon={FiDollarSign}
          color="red"
          to="/admin/payments"
        />
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
        <ChartCard
          title="Users Over Time"
          data={usersOverTime}
          color="#3182ce"
          valueLabel="Total Users"
        />
        <ChartCard
          title="Tasks Over Time"
          data={bountiesOverTime}
          color="#38a169"
          valueLabel="Total Tasks"
        />
        <ChartCard
          title="Value Settled Over Time"
          data={settledOverTime}
          color="#dd6b20"
          valueLabel="Total Settled"
          formatValue={formatDollar}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {/* Recent Users */}
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box px={4} py={3} borderBottomWidth={1} borderColor={borderColor}>
            <Text fontWeight="semibold" fontSize="sm">Recent Signups</Text>
          </Box>
          {users.slice(0, 5).map((user) => (
            <RecentItem
              key={user.id}
              label={user.anonymizedUsername || user.email}
              sublabel={user.email}
              time={formatRelativeTime(user.createdAt)}
              to={`/admin/users/${user.id}`}
            />
          ))}
        </Box>

        {/* Recent Bounties */}
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box px={4} py={3} borderBottomWidth={1} borderColor={borderColor}>
            <Text fontWeight="semibold" fontSize="sm">Recent Tasks</Text>
          </Box>
          {bounties.slice(0, 5).map((bounty) => (
            <RecentItem
              key={bounty.id}
              label={bounty.title}
              sublabel={`${bounty.status} - ${bounty.creatorAnonymizedUsername}`}
              time={formatRelativeTime(bounty.createdAt)}
              to={`/admin/tasks`}
            />
          ))}
        </Box>

        {/* Recent Disputes */}
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box px={4} py={3} borderBottomWidth={1} borderColor={borderColor}>
            <Text fontWeight="semibold" fontSize="sm">Recent Disputes</Text>
          </Box>
          {cases.length === 0 ? (
            <Box px={4} py={3}><Text fontSize="sm" color="gray.400">No disputes</Text></Box>
          ) : cases.slice(0, 5).map((c) => (
            <RecentItem
              key={c.id}
              label={`Dispute: ${c.reason?.slice(0, 50) || 'No reason'}${c.reason?.length > 50 ? '...' : ''}`}
              sublabel={c.status}
              time={formatRelativeTime(c.initiatedAt)}
              to={`/admin/disputes/${c.id}`}
            />
          ))}
        </Box>

        {/* Recent Support Tickets */}
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box px={4} py={3} borderBottomWidth={1} borderColor={borderColor}>
            <Text fontWeight="semibold" fontSize="sm">Recent Support Tickets</Text>
          </Box>
          {tickets.length === 0 ? (
            <Box px={4} py={3}><Text fontSize="sm" color="gray.400">No tickets</Text></Box>
          ) : tickets.slice(0, 5).map((t) => (
            <RecentItem
              key={t.id}
              label={t.subject}
              sublabel={`${t.status} - ${t.creatorUsername}`}
              time={formatRelativeTime(t.createdAt)}
              to={`/admin/support/${t.id}`}
            />
          ))}
        </Box>
      </SimpleGrid>
    </VStack>
  );
}
